import { useQueries } from '@tanstack/react-query';

import { patientQueries } from '@/features/patient-record/queries';

/**
 * Resolves a set of person guids to display names via the shared
 * `patientQueries.detail` cache — one cached query per distinct guid, so a
 * record already open elsewhere costs nothing here. Returns a `guid → name`
 * map; unresolved guids (loading, or 403/404) are simply absent and callers
 * render a fallback label.
 */
export function usePersonNames(guids: readonly string[]): ReadonlyMap<string, string> {
  const unique = Array.from(new Set(guids.filter((guid) => guid.length > 0)));
  const results = useQueries({
    queries: unique.map((guid) => ({
      ...patientQueries.detail(guid),
      staleTime: 5 * 60_000,
    })),
  });

  const map = new Map<string, string>();
  unique.forEach((guid, index) => {
    const patient = results[index]?.data;
    if (patient === undefined) return;
    const name = joinName(patient.demographics.firstName, patient.demographics.lastName);
    if (name !== null) map.set(guid, name);
  });
  return map;
}

function joinName(first: string | null, last: string | null): string | null {
  const parts = [first, last].filter((p): p is string => p !== null && p.trim().length > 0);
  return parts.length > 0 ? parts.join(' ') : null;
}
